import {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import axios from 'axios';
import {API, axiosInstance, useAuth, OrganizationAuthority} from '../App';
import AdminPortalPreview from '../pages/AdminPortalPreview';
import {adminOrgBase, adminWorkspaceIdBase} from '../config/adminPortalRoutes';
import {Button} from '../components/ui/button';
import {useSupportSession} from './SupportSessionProvider';
import SupportWorkspace from './SupportWorkspace';

export function OrganizationWorkspaceEntry({organization}) {
  const {user} = useAuth();
  const navigate = useNavigate();
  if (user?.role !== 'super_admin') return null;
  let base;
  try { base = adminOrgBase(organization?.subdomain); }
  catch {
    if (organization?.subdomain || organization?.status !== 'active' || !organization?.approved_by || !organization?.approved_at) return null;
    try { base = adminWorkspaceIdBase(organization.org_id); } catch { return null; }
  }
  return <Button variant="outline" onClick={()=>navigate(`${base}/today`)}>View as organization</Button>;
}

// Context belongs to this route/tab, never localStorage, cookies or the actor's
// persisted role. Only our API receives the selector; auth refresh stays canonical.
export function workspaceRequest(config, api, slug, workspaceOrgId) {
  const base = new URL(api, window.location.origin);
  const url = new URL(axios.getUri(config), window.location.origin);
  if (url.origin === base.origin && url.pathname.startsWith(`${base.pathname}/`) && !url.pathname.startsWith(`${base.pathname}/auth/`)) {
    config.headers = {...config.headers, [workspaceOrgId !== undefined ? 'X-Organization-Workspace-ID' : 'X-Organization-Workspace']: workspaceOrgId ?? slug};
  }
  return config;
}

export default function OrganizationWorkspace() {
  const support = useSupportSession();
  const {workspaceOrgId} = useParams();
  if (support && !['idle', 'ended'].includes(support.status)) {
    if (workspaceOrgId !== undefined) return <p role="alert">Exit the support session before opening an ID workspace.</p>;
    return <SupportWorkspace/>;
  }
  return <OrdinaryOrganizationWorkspace/>;
}

function OrdinaryOrganizationWorkspace() {
  const auth = useAuth();
  const {orgSlug, workspaceOrgId} = useParams();
  const selector = workspaceOrgId ?? orgSlug;
  const header = workspaceOrgId !== undefined ? 'X-Organization-Workspace-ID' : 'X-Organization-Workspace';
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [error, setError] = useState('');
  useEffect(()=>{
    if (auth.user?.role !== 'super_admin') return undefined;
    let active = true;
    setState(null); setError('');
    try { if (workspaceOrgId !== undefined) adminWorkspaceIdBase(workspaceOrgId); else adminOrgBase(orgSlug); }
    catch { setError('Invalid organization workspace.'); return undefined; }
    const headers = {[header]: selector};
    const clients = [...new Set([axios, axiosInstance])];
    const ids = clients.map(client=>client.interceptors.request.use(config=>workspaceRequest(config, API, orgSlug, workspaceOrgId)));
    axios.get(`${API}/organizations/${workspaceOrgId !== undefined ? 'admin-workspace' : 'admin-portal'}/context/${encodeURIComponent(selector)}`, {headers})
      .then(({data})=>{
        if (!data?.organization?.org_id || (workspaceOrgId !== undefined ? data.organization.org_id !== workspaceOrgId : data.organization.subdomain !== orgSlug)) throw new Error('Invalid context');
        if (active) setState({organization:data.organization,selector,header,token:auth.token,actorId:auth.user.user_id});
      }).catch(()=>{if(active)setError('Organization access could not be confirmed.');});
    return ()=>{active=false; clients.forEach((client,index)=>client.interceptors.request.eject(ids[index]));};
  },[orgSlug,workspaceOrgId,selector,header,auth.user?.user_id,auth.user?.role,auth.token]);
  if (auth.user?.role !== 'super_admin') return workspaceOrgId !== undefined ? <p role="alert">Super administrator required</p> : <AdminPortalPreview mode="organization"/>;
  const exit = async()=>{
    try {
      await axios.post(`${API}/organizations/admin-workspace/exit`, {}, {headers:{[header]:selector}});
      navigate(`/platform/organizations/${state?.organization.org_id || ''}`, {replace:true});
    } catch {setError('Exit audit could not be confirmed. Retry exit, or return to platform.');}
  };
  return <><section aria-label="Superadmin organization workspace" className="sticky top-0 z-50 border-b border-amber-300 bg-amber-50 p-4 text-sm">
    <strong>{auth.user.name || auth.user.user_id} · Super administrator · {state?.organization.name || selector} · Read/write organization workspace</strong>
    <Button onClick={exit}>Exit organization workspace</Button>
    <Button variant="outline" onClick={()=>navigate('/platform/organizations',{replace:true})}>Return to platform</Button>
    {error && <p role="alert">{error}</p>}
  </section>{state?.selector === selector && state?.header === header && state?.actorId === auth.user.user_id && state?.token === auth.token ? <OrganizationAuthority organization={state.organization}><AdminPortalPreview mode="organization"/></OrganizationAuthority> : !error && <p role="status">Authorizing organization workspace…</p>}</>;
}
